import { Component, Vue, Prop } from 'vue-property-decorator';
import { applyClasses } from '../../lib';

type Variant = 'determinate' | 'indeterminate' | 'static';

type Classes = {
  root: string | object;
  svg: string | object;
  circle: string | object;
};

type Color = 'primary' | 'secondary' | 'inherit';

function getRelativeValue(value: number, min: number, max: number) {
  const clampedValue = Math.min(Math.max(min, value), max);
  return (clampedValue - min) / (max - min);
}

function easeOut(t: number) {
  t = getRelativeValue(t, 0, 1);
  // https://gist.github.com/gre/1650294
  t = (t -= 1) * t * t + 1;
  return t;
}

function easeIn(t: number) {
  return t * t;
}

@Component
export default class ClCircularProgress extends Vue {
  /**
   * Override or extend the styles applied to the component
   */
  @Prop({
    type: Object as () => Classes,
    default: () => ({})
  })
  public classes!: Classes;

  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  @Prop({
    type: String as () => Color,
    default: 'primary'
  })
  public color!: Color;

  /**
   * The size of the circle.
   */
  @Prop({
    type: [Number, String],
    default: 40
  })
  public size!: number | string;

  /**
   * The thickness of the circle.
   */
  @Prop({
    type: Number,
    default: 3.6
  })
  public thickness!: number;

  /**
   * The value of the progress indicator for the determinate and static variants. Value between 0 and 100.
   */
  @Prop({
    type: Number,
    default: 0
  })
  public value!: number;

  /**
   * The variant to use. Use indeterminate when there is no progress value.
   */
  @Prop({
    type: String as () => Variant,
    default: 'indeterminate'
  })
  public variant!: Variant;

  public circleSize: number = 44;

  get rootClasses(): object {
    return applyClasses(
      {
        'cl-circular-progress': true,
        [`cl-circular-progress--color-${this.color}`]: this.color !== 'inherit',
        'cl-circular-progress--static': this.variant === 'static',
        [`cl-circular-progress--variant-${this.variant}`]: this.variant
      },
      this.classes.root
    );
  }

  get svgClasses(): object {
    return applyClasses(
      { 'cl-circular-progress__svg': true },
      this.classes.svg
    );
  }

  get svgViewBox(): string {
    const s = this.circleSize;
    return `${s / 2} ${s / 2} ${s} ${s}`;
  }

  get rootStyle(): object {
    let transform;
    if (this.variant === 'determinate') {
      transform = `rotate(${(easeOut(this.value / 70) * 270).toFixed(3)}deg)`;
    } else if (this.variant !== 'indeterminate') {
      transform = 'rotate(-90deg)';
    }
    return {
      width: this.size + 'px',
      height: this.size + 'px',
      transform
    };
  }

  get circleClasses(): object {
    return applyClasses(
      {
        'cl-circular-progress__circle': true,
        [`cl-circular-progress__circle--variant-${this.variant}`]: true
      },
      this.classes.circle
    );
  }

  get circleStyle(): object {
    const style: any = {};
    if (this.variant === 'determinate' || this.variant === 'static') {
      const circumference =
        2 * Math.PI * ((this.circleSize - this.thickness) / 2);
      style.strokeDasharray = circumference.toFixed(3);
      if (this.variant === 'static') {
        style.strokeDashoffset = `${(
          ((100 - this.value) / 100) *
          circumference
        ).toFixed(3)}px`;
      } else {
        style.strokeDashoffset = `${(
          easeIn((100 - this.value) / 100) * circumference
        ).toFixed(3)}px`;
      }
    }
    return style;
  }
}
